import React, { useEffect } from 'react';
import styled, { css, CSSProperties } from 'styled-components';

import { useDocument } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { IstoreCommon } from '@typings/store/storeCommon.interface';
import { useRecoilValue } from 'recoil';
import { currentUserWalletState } from '@atom/setting/user';
import { User } from '@typings/setting/userType.interface';
import ExplorationPoint from './ExplorationPoint';
import { defaultCurrencyState } from '@atom/setting/homeSettingState';

export function formatPrice(value: number | string): string {
  const number = Number(value);
  if (isNaN(number)) return 'NaN';

  const rounded = Math.round(number * 10) / 10;
  const hasDecimal = rounded % 1 !== 0;

  return new Intl.NumberFormat(navigator.language, {
    minimumFractionDigits: hasDecimal ? 1 : 0,
    maximumFractionDigits: 1,
  }).format(rounded);
}

export const walletSizeMap = {
  xl: css``,
  lg: css``,
  md: css`
    padding: 6px;
    height: 40px;
  `,
  sm: css`
    padding: 4px 4px;
    font-size: 13px;
  `,
};

const PriceBox = styled.div<{
  size: keyof typeof walletSizeMap;
}>`
  width: 100%;
  border-radius: var(--cardBorderRadius);
  background-color: var(--cardBackgroundColor);
  border: 1px solid var(--cardLineColor);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  position: relative;
  min-height: 32px;
  font-size: 14px;
  ${({ size }) => walletSizeMap[size]};

  & > span {
    white-space: nowrap;
    flex-shrink: 0;
    z-index: 1;
  }

  img {
    max-height: 30px;
  }

`;

const MyWallet = styled.div<{
  size: keyof typeof walletSizeMap;
}>`
  padding: 8px;
  background-color: var(--subColor);
  color: var(--subTextColor);
  border: 1px solid var(--lineColor);
  corner-shape: var(--cardCornerShape);
  border-radius: 8px;
  font-weight: 800;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  position: relative;
  flex-shrink: 0;
  ${({ size }) => walletSizeMap[size]};

  &.exploration-point {
    flex-direction: column;
    gap: 2px;
    flex-shrink: 0;
  }
`;

const absoluteStyle = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 8px;
`;

const MoneyImg = styled.img<{ isAbsolute: boolean }>`
  object-fit: contain;
  flex-shrink: 0;
  max-height: 40px;
  ${({ isAbsolute }) => isAbsolute && absoluteStyle};

  @media (max-width: 480px) {
    max-width: 20px;
  }
`;

const WalletContainer = styled.div<{ $direction?: 'row' | 'column' }>`
  display: flex;
  gap: 6px;
  width: 100%;

  ${({ $direction }) =>
    $direction === 'row'
      ? css`
          flex-direction: row;
          align-items: stretch;
          > * {
            flex: 1;
            width: 0 !important;
            min-width: 0;
            height: 100%;
            box-sizing: border-box;
          }
        `
      : css`
          flex-direction: column;
          align-items: stretch;
        `}
`;

type Props = {
  price?: number;
  showMyWalet?: boolean;
  uid?: string;
  id?: string;
  setDisabled?: any;
  style?: CSSProperties;
  containerStyle?: CSSProperties;
  size?: keyof typeof walletSizeMap;
  showPoint?: boolean;
  isAbsolute?: boolean;
  /** 아이템 정렬 방향. 미지정 시 column */
  direction?: 'row' | 'column';
};
function WalletPrice({
  price,
  showMyWalet = false,
  uid,
  id,
  setDisabled,
  containerStyle,
  style,
  size = 'sm',
  showPoint = false,
  isAbsolute = true,
  direction,
}: Props) {
  const currentUserWallet = useRecoilValue(currentUserWalletState);
  const defaultCurrency = useRecoilValue(defaultCurrencyState);
  const { moneyUnit, moneyImgURL } = defaultCurrency;

  const { data: userData } = useDocument<User>(
    `${CollectionPath.USERS}/${uid}`,
  );


  useEffect(() => {
    if (setDisabled && currentUserWallet && price && price > 0) {
      setDisabled(Number(currentUserWallet) < price);
    }
  }, [currentUserWallet, price, setDisabled]);


  return (
    <WalletContainer $direction={direction} style={containerStyle}>
      {(price || price === 0) && (
        <PriceBox style={style} size={size}>
          {moneyImgURL && (
            <MoneyImg src={moneyImgURL} alt="가격" isAbsolute={isAbsolute} />
          )}
          <span>{formatPrice(price)} {moneyUnit}</span>
        </PriceBox>
      )}
      {showMyWalet && (
        <MyWallet style={style} size={size}>
          {moneyImgURL && (
            <MoneyImg src={moneyImgURL} alt="가격" isAbsolute={isAbsolute} />
          )}
          {!uid && !id && currentUserWallet ? (
            <>내 지갑 {formatPrice(currentUserWallet || 0)}</>
          ) : (
            <>{userData?.wallet}</>
          )}{' '}
          {moneyUnit}
        </MyWallet>
      )}
      {showPoint && <ExplorationPoint style={style} size={size} />}
    </WalletContainer>
  );
}

export default React.memo(WalletPrice);
